// [S-385] Draft Workspace index — lists the caller's drafts (owned + shared)
// newest-first; each row links to the editor. The "+ New draft" CTA opens a
// modal that creates an empty draft and navigates into it.
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Dialog, DialogClose, LoadingState, PageHead, Pill, Select } from "../components";
import { DraftActionsMenu } from "../components/drafts/DraftActionsMenu";
import { useSession } from "../auth/AuthProvider";
import { getApiErrorMessage } from "../data/api";
import {
  useClassifyDraft,
  useCreateDraft,
  useDrafts,
  useSaveVersion,
  type ClassifyDraftResult,
  type Draft,
  type DealType,
  type GuildCode,
} from "../data/draftsQuery";
import { useTenantUsersList } from "../data/tenantsQuery";
import type { RecordRef } from "../data/chatQuery";
import { RecordDrawerProvider, useRecordDrawer } from "./agents/RecordDrawer";

const UPLOAD_ACCEPT = ".docx,.pdf,.txt,.html,.htm";

const DEAL_TYPES: { value: DealType; label: string }[] = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: { value: GuildCode; label: string }[] = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

export function Drafts(): JSX.Element {
  const session = useSession();
  const { data, isLoading, isFetching, isError, error } = useDrafts();
  const drafts = data ?? [];
  const [createOpen, setCreateOpen] = useState(false);
  // Holds the classify result while the user reviews/edits the suggestion in
  // the dialog. Null = no pending upload.
  const [pending, setPending] = useState<ClassifyDraftResult | null>(null);
  const classify = useClassifyDraft();
  const fileRef = useRef<HTMLInputElement>(null);
  // Cards name whoever drafted the doc, not just the caller's own role.
  // Drafts in this list are always same-tenant, so the roster (persisted
  // displayName/email) resolves it with no extra lookup — same as the share
  // modal in DraftDetail.
  const tenantUsers =
    useTenantUsersList(drafts[0]?.tenantId ?? "").data?.users ?? [];
  function ownerLabel(draft: Draft): string {
    if (draft.ownerUid === session?.uid) return "Drafted by you";
    const u = tenantUsers.find((row) => row.uid === draft.ownerUid);
    const name = u?.displayName ?? u?.email;
    return name === undefined ? "Shared with you" : `Drafted by ${name}`;
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (file === undefined) return;
    classify.mutate(
      { file },
      { onSuccess: (result) => setPending(result) }
    );
  }

  return (
    <RecordDrawerProvider>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot cert" aria-hidden="true" />
            Draft Workspace
          </>
        }
        title={
          <>
            Your drafts, <em>versioned.</em>
          </>
        }
        sub={
          isLoading
            ? "Loading…"
            : `${drafts.length.toLocaleString()} draft${
                drafts.length === 1 ? "" : "s"
              } you own or collaborate on.`
        }
        cta={
          <div className="dft__head__actions">
            <input
              ref={fileRef}
              type="file"
              accept={UPLOAD_ACCEPT}
              onChange={handleFile}
              hidden
              data-testid="draft-upload-input"
            />
            <Button
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={classify.isPending}
            >
              {classify.isPending ? "Classifying…" : "Upload & classify"}
            </Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + New draft
            </Button>
          </div>
        }
      />

      {classify.isError && (
        <Alert label="Couldn't read that file" tone="error">
          {getApiErrorMessage(classify.error) ?? classify.error.message}
        </Alert>
      )}

      {(isLoading || (isFetching && drafts.length === 0)) && (
        <LoadingState label="Loading drafts…" />
      )}

      {isError && (
        <Alert label="Couldn't load drafts" tone="error">
          {error.message}
        </Alert>
      )}

      {!isLoading && !isFetching && !isError && drafts.length === 0 && (
        <div className="prec__state">
          <div className="prec__state__title">No drafts yet</div>
          <p>
            Create a draft to edit it in the rich-text editor, save versions,
            run a review against the certified templates + your studio
            precedents, and share with your team.
          </p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Create your first draft →
          </Button>
        </div>
      )}

      {!isLoading && !isError && drafts.length > 0 && (
        <div className="dft__index" role="list">
          {drafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              isOwner={d.ownerUid === session?.uid}
              ownerLabel={ownerLabel(d)}
            />
          ))}
        </div>
      )}

      <CreateDraftModal open={createOpen} onOpenChange={setCreateOpen} />
      {pending !== null && (
        <ConfirmClassificationModal
          pending={pending}
          onClose={() => setPending(null)}
        />
      )}
    </RecordDrawerProvider>
  );
}

// [S-387] After upload+classify, let the user review and edit the suggested
// title / deal type / guild before the draft is created. On confirm, creates
// the draft then saves the extracted content as v1 and opens the editor.
function ConfirmClassificationModal({
  pending,
  onClose,
}: {
  pending: ClassifyDraftResult;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateDraft();
  const saveVersion = useSaveVersion();
  const { suggestion } = pending;
  const [title, setTitle] = useState(suggestion.title);
  const [dealType, setDealType] = useState<DealType>(suggestion.dealType);
  const [guild, setGuild] = useState<GuildCode>(suggestion.guild);
  const busy = create.isPending || saveVersion.isPending;
  const err = create.error ?? saveVersion.error;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (title.trim().length === 0 || busy) return;
    create.mutate(
      { title: title.trim(), dealType, guild },
      {
        onSuccess: (draft) =>
          saveVersion.mutate(
            { draftId: draft.id, content: pending.content },
            {
              onSuccess: () => {
                onClose();
                navigate(`/drafts/${encodeURIComponent(draft.id)}`);
              },
            }
          ),
      }
    );
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
      title="Review classification"
      description={`We classified "${pending.filename}". Adjust anything before creating the draft.`}
    >
      <form onSubmit={handleSubmit} className="dft__form">
        <div className="dft__classify__note">
          {suggestion.error !== undefined ? (
            <span>
              Auto-classification unavailable — defaults applied. Set the deal
              type and guild manually.
            </span>
          ) : (
            <span>
              Suggested from the document
              {suggestion.confidence !== null
                ? ` · ${(suggestion.confidence * 100).toFixed(0)}% confidence`
                : ""}
              {suggestion.anchorCBA !== null
                ? ` · ${suggestion.anchorCBA}`
                : ""}
              . Edit if needed.
            </span>
          )}
        </div>
        <label className="dft__field">
          <span className="dft__field__lbl">Title</span>
          <input
            className="dft__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </label>
        <label className="dft__field">
          <span className="dft__field__lbl">Deal type</span>
          <Select
            label="Deal type"
            value={dealType}
            clearable={false}
            onValueChange={(v) => v !== undefined && setDealType(v)}
            options={DEAL_TYPES}
          />
        </label>
        <label className="dft__field">
          <span className="dft__field__lbl">Guild</span>
          <Select
            label="Guild"
            value={guild}
            clearable={false}
            onValueChange={(v) => v !== undefined && setGuild(v)}
            options={GUILDS}
          />
        </label>
        {err !== null && (
          <div className="prec__state" role="alert">
            {getApiErrorMessage(err) ?? err.message}
          </div>
        )}
        <div className="dft__form__actions">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={title.trim().length === 0 || busy}
          >
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// [S-413 follow-up] Builds this draft's RecordRef for the shared preview
// drawer — same widget Templates/Precedents open, so browsing any of the
// three feels consistent. The id stays internal (never rendered raw).
function draftRef(draft: Draft): RecordRef {
  return {
    type: "draft",
    id: draft.id,
    tenantId: draft.tenantId,
    title: draft.title || "Untitled draft",
    subtitle: `${prettyDealType(draft.dealType)} · ${prettyGuild(draft.guild)}`,
    uri: `csai://draft/${draft.tenantId}/${draft.id}`,
  };
}

function DraftCard({
  draft,
  isOwner,
  ownerLabel,
}: {
  draft: Draft;
  isOwner: boolean;
  ownerLabel: string;
}): JSX.Element {
  const { openRecord } = useRecordDrawer();
  return (
    <div className="dft__card" role="listitem" data-testid="draft-row">
      <div className="dft__card__menu">
        <DraftActionsMenu draft={draft} isOwner={isOwner} />
      </div>
      <Button
        variant="bare"
        onClick={() => openRecord(draftRef(draft))}
        className="dft__card__body"
      >
        <header className="dft__card__head">
          <span className="dft__card__title">
            {draft.title || "Untitled draft"}
          </span>
        </header>
        <div className="dft__card__meta">
          {prettyGuild(draft.guild)} · {prettyDealType(draft.dealType)} ·{" "}
          {formatDate(draft.updatedAt)}
        </div>
        <footer className="dft__card__foot">
          <Pill variant="low">v{draft.currentVersion}</Pill>
          {draft.contentLength > 0 && (
            <span
              className="dft__card__size"
              title="Approximate document size — larger drafts run to multiple pages"
            >
              {formatSize(draft.contentLength)}
            </span>
          )}
          <span className="dft__card__role">{ownerLabel}</span>
          {draft.sharedWith.length > 0 && (
            <span className="dft__card__shared">
              {draft.sharedWith.length} collaborator
              {draft.sharedWith.length === 1 ? "" : "s"}
            </span>
          )}
        </footer>
      </Button>
    </div>
  );
}

function CreateDraftModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateDraft();
  const [title, setTitle] = useState("");
  const [dealType, setDealType] = useState<DealType>("feature_film");
  const [guild, setGuild] = useState<GuildCode>("SAG");

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (title.trim().length === 0 || create.isPending) return;
    create.mutate(
      { title: title.trim(), dealType, guild },
      {
        onSuccess: (draft) => {
          onOpenChange(false);
          setTitle("");
          navigate(`/drafts/${encodeURIComponent(draft.id)}`);
        },
      }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New draft"
      description="Create a draft, then edit and review it. Deal type + guild drive which certified standard the review compares against."
    >
      <form onSubmit={handleSubmit} className="dft__form">
        <label className="dft__field">
          <span className="dft__field__lbl">Title</span>
          <input
            className="dft__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Lead actor agreement — 2026"
            autoFocus
          />
        </label>
        <label className="dft__field">
          <span className="dft__field__lbl">Deal type</span>
          <Select
            label="Deal type"
            value={dealType}
            clearable={false}
            onValueChange={(v) => v !== undefined && setDealType(v)}
            options={DEAL_TYPES}
          />
        </label>
        <label className="dft__field">
          <span className="dft__field__lbl">Guild</span>
          <Select
            label="Guild"
            value={guild}
            clearable={false}
            onValueChange={(v) => v !== undefined && setGuild(v)}
            options={GUILDS}
          />
        </label>
        {create.isError && (
          <div className="prec__state" role="alert">
            {getApiErrorMessage(create.error) ?? create.error.message}
          </div>
        )}
        <div className="dft__form__actions">
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            variant="primary"
            disabled={title.trim().length === 0 || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create draft"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// Human-readable size from the draft's stored content-character count.
// Content is editor HTML, so this is an approximate document size — enough
// to spot at a glance which drafts are long / multi-page.
function formatSize(chars: number): string {
  if (chars < 1024) return `${chars} B`;
  if (chars < 1024 * 1024) return `${(chars / 1024).toFixed(1)} KB`;
  return `${(chars / (1024 * 1024)).toFixed(1)} MB`;
}

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
